import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { adminListTestimonials, adminCreateTestimonial, adminUpdateTestimonial, adminDeleteTestimonial } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, LogOut } from "lucide-react";

const empty = { name: "", title: "", organization: "", quote: "", photo_url: "", approved: true, order: 0 };

export default function AdminTestimonials() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await adminListTestimonials()); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.quote.trim()) { toast.error("Name and quote are required."); return; }
    setCreating(true);
    try {
      await adminCreateTestimonial(form);
      setForm(empty);
      toast.success("Added");
      await load();
    } catch { toast.error("Failed to add"); }
    finally { setCreating(false); }
  };

  const patch = async (id, changes) => {
    try { await adminUpdateTestimonial(id, changes); await load(); }
    catch { toast.error("Update failed"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await adminDeleteTestimonial(id); await load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const doLogout = async () => { await signOut(); navigate("/"); };
  const approvedCount = items.filter((t) => t.approved).length;

  return (
    <div className="min-h-screen bg-[var(--layor-ivory)]" data-testid="page-admin-testimonials">
      <div className="border-b border-[var(--layor-border)] bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-[var(--layor-muted)] hover:text-[var(--layor-navy)] inline-flex items-center gap-1 text-sm"><ArrowLeft size={14} /> Admin</Link>
            <span className="text-[10px] uppercase tracking-[0.28em] text-[var(--layor-muted)] border-l border-[var(--layor-border)] pl-4">Testimonials</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-[var(--layor-muted)] hover:text-[var(--layor-navy)]">View site</Link>
            <span className="text-sm text-[var(--layor-navy)] hidden sm:inline">{user?.email}</span>
            <button onClick={doLogout} className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-10 py-10">
        <div className="max-w-2xl">
          <div className="eyebrow">Client Voices</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl text-[var(--layor-navy)]">Add real, approved testimonials.</h1>
          <p className="mt-3 text-sm text-[var(--layor-muted)]">
            The homepage testimonials section stays hidden until you have <strong>3 or more approved</strong> entries. Currently: {approvedCount} approved.
          </p>
        </div>

        <div className="mt-8 grid md:grid-cols-12 gap-6">
          <div className="md:col-span-5">
            <div className="bg-white border border-[var(--layor-border)] rounded-2xl p-5 sticky top-6">
              <div className="eyebrow text-[var(--layor-pink)]">Add a testimonial</div>
              <form onSubmit={onCreate} className="mt-4 space-y-3">
                <div><Label>Name *</Label><Input required data-testid="t-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Title</Label><Input data-testid="t-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Superintendent" /></div>
                  <div><Label>Organization</Label><Input data-testid="t-org" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="ABC School District" /></div>
                </div>
                <div><Label>Photo URL (optional)</Label><Input data-testid="t-photo" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://…" /></div>
                <div><Label>Quote *</Label><Textarea required rows={4} data-testid="t-quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} /></div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch checked={form.approved} onCheckedChange={(v) => setForm({ ...form, approved: v })} />
                    <span className="text-sm text-[var(--layor-navy)]">Approved (visible)</span>
                  </div>
                  <button type="submit" disabled={creating} className="btn-primary inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-60" data-testid="t-submit">
                    <Plus size={12} /> {creating ? "Adding…" : "Add"}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="md:col-span-7 space-y-3">
            {loading ? (
              <div className="text-sm text-[var(--layor-muted)]">Loading…</div>
            ) : items.length === 0 ? (
              <div className="border border-dashed border-[var(--layor-border)] rounded-2xl p-8 text-center bg-white text-sm text-[var(--layor-muted)]">No testimonials yet.</div>
            ) : items.map((t) => (
              <div key={t.id} className="bg-white border border-[var(--layor-border)] rounded-2xl p-5" data-testid={`t-item-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-[var(--layor-navy)]">{t.name}</div>
                    <div className="text-xs text-[var(--layor-muted)]">{[t.title, t.organization].filter(Boolean).join(" · ")}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-xs text-[var(--layor-navy)]">
                      <Switch checked={t.approved} onCheckedChange={(v) => patch(t.id, { approved: v })} /> {t.approved ? "Approved" : "Hidden"}
                    </div>
                    <button onClick={() => onDelete(t.id)} className="text-red-500 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
                  </div>
                </div>
                <p className="mt-3 text-sm text-[var(--layor-navy)] leading-relaxed">{t.quote}</p>
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  <Input className="text-xs" placeholder="Photo URL" value={t.photo_url || ""} onBlur={(e) => patch(t.id, { photo_url: e.target.value })} onChange={(e) => setItems((all) => all.map((x) => x.id === t.id ? { ...x, photo_url: e.target.value } : x))} />
                  <Input className="text-xs" placeholder="Order (0 = first)" type="number" value={t.order || 0} onBlur={(e) => patch(t.id, { order: parseInt(e.target.value || "0", 10) })} onChange={(e) => setItems((all) => all.map((x) => x.id === t.id ? { ...x, order: e.target.value } : x))} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
